import * as React from "react";

import type { PtiSummary, Rating } from "@volley/data";

import fetchApi, { logFetchError } from "../../util/fetchApi";
import { useSelectedSport } from "../common/context/sport";

/**
 * Whether `hasAptaId` and `rating` are an answer yet.
 *
 * Three states, not a `loading` boolean, because the failed case wants different
 * UI from the pending one: while pending, PTI UI is withheld so it cannot be
 * mistapped; once failed, it is shown and falls through to /player-pti, which
 * resolves the player id itself and reports its own errors. Collapsing the two
 * hides the entry outright, leaving a platform tennis player no way to reach
 * their PTI from home.
 */
export type PtiState = "loading" | "loaded" | "error";

export interface HomePtiValue {
    /** The player's computed PTI, or null when there is none. */
    rating: Rating | null;
    /**
     * Whether the player has an APTA number on record. This — not `rating` — is
     * what gates offering to collect one: a linked player with no computable PTI
     * has a null `rating`, and asking them to re-enter a number they already
     * gave is exactly the bug this context was built to end (issue #8335).
     */
    hasAptaId: boolean;
    /**
     * Only "loaded" licenses reading `hasAptaId: false` as "this player has not
     * linked a number" — in the other two states it is a placeholder, not an
     * answer, and acting on it is the bug this context exists to prevent.
     */
    state: PtiState;
    /** Re-read after something changes the answer — linking an APTA number. */
    refetch: () => Promise<void>;
}

const HomePtiContext = React.createContext<HomePtiValue | null>(null);

/**
 * The home screen's PTI state, fetched once.
 *
 * PTI used to arrive as the `rating` field on `/api/stats/at-a-glance`, which
 * made every player in every sport wait on a third-party APTA lookup — measured
 * at 3.3s for the players whose PTI cannot be computed — for a field only
 * platform tennis renders, and left one flaky external dependency able to
 * discard the whole home screen's data. `GET /api/pti/summary` answers it alone.
 */
export function HomePtiProvider({
    children,
}: {
    children: React.ReactNode;
}): React.JSX.Element {
    const { selected: sport } = useSelectedSport();

    // PTI is a platform tennis rating, so nobody else pays for the request.
    const shouldFetch = sport === "PLATFORM_TENNIS";

    const [rating, setRating] = React.useState<Rating | null>(null);
    const [hasAptaId, setHasAptaId] = React.useState(false);
    // Starts pending so consumers withhold rather than flash.
    const [state, setState] = React.useState<PtiState>("loading");

    // A ref, not an effect-scoped flag: `load` is also called via refetch(),
    // whose result can land after unmount just as the initial fetch's can.
    const mounted = React.useRef(true);
    React.useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const load = React.useCallback(async (): Promise<void> => {
        if (!shouldFetch) {
            // Settled, not pending: there is nothing to wait for. `hasAptaId`
            // false is honest here — this player is not in a sport that asks.
            if (mounted.current) {
                setRating(null);
                setHasAptaId(false);
                setState("loaded");
            }
            return;
        }
        setState("loading");
        try {
            const res = await fetchApi<PtiSummary>("/api/pti/summary");
            if (mounted.current) {
                setRating(res.rating);
                setHasAptaId(res.hasAptaId);
                setState("loaded");
            }
        } catch (err) {
            logFetchError(err, "Failed to load PTI summary");
            if (mounted.current) {
                setRating(null);
                setHasAptaId(false);
                // "error", never "loaded": we did not learn that this player has
                // no APTA number, so nothing may act as though we did.
                setState("error");
            }
        }
    }, [shouldFetch]);

    React.useEffect(() => {
        void load();
    }, [load]);

    const value = React.useMemo(
        () => ({ rating, hasAptaId, state, refetch: load }),
        [rating, hasAptaId, state, load],
    );

    return (
        <HomePtiContext.Provider value={value}>
            {children}
        </HomePtiContext.Provider>
    );
}

/** The home screen's shared PTI state. Must be used under HomePtiProvider. */
export function useHomePti(): HomePtiValue {
    const context = React.useContext(HomePtiContext);
    if (!context) {
        throw new Error("useHomePti must be used within HomePtiProvider");
    }
    return context;
}
